// Tiny markdown-lite renderer for blog posts. Supports:
// ## Heading, ### Subheading, **bold**, *italic*, [link](url), lists, paragraphs.
import React from 'react';

function inline(text) {
  // Order matters
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      nodes.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      nodes.push(
        <code key={key++} className="bg-zinc-100 border border-zinc-300 px-1.5 py-0.5 font-mono-plex text-[0.9em]">
          {tok.slice(1, -1)}
        </code>
      );
    } else if (tok.startsWith('*')) {
      nodes.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith('[')) {
      const [, label, url] = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      const isInternal = url.startsWith('/');
      nodes.push(
        <a
          key={key++}
          href={url}
          className="underline underline-offset-4 hover:no-underline font-semibold"
          {...(isInternal ? {} : { target: '_blank', rel: 'noopener' })}
        >
          {label}
        </a>
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export default function Markdown({ children }) {
  const lines = children.trim().split('\n');
  const blocks = [];
  let currentList = null;
  let currentPara = [];

  const flushPara = (k) => {
    if (currentPara.length) {
      blocks.push(
        <p key={k} className="text-zinc-700 leading-relaxed mb-4">
          {inline(currentPara.join(' '))}
        </p>
      );
      currentPara = [];
    }
  };
  const flushList = (k) => {
    if (currentList) {
      blocks.push(
        <ul key={k} className="list-disc pl-6 space-y-1 text-zinc-700 mb-5">
          {currentList.map((li) => <li key={li}>{inline(li)}</li>)}
        </ul>
      );
      currentList = null;
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const key = `l${idx}`;
    if (!line.trim()) {
      flushPara(key);
      flushList(key);
      return;
    }
    if (line.startsWith('## ')) {
      flushPara(key);
      flushList(key);
      blocks.push(
        <h2 key={key} className="font-display font-bold text-2xl md:text-3xl mt-10 mb-4 text-slate-950">
          {inline(line.slice(3))}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      flushPara(key);
      flushList(key);
      blocks.push(
        <h3 key={key} className="font-display font-semibold text-xl mt-6 mb-3 text-slate-950">
          {inline(line.slice(4))}
        </h3>
      );
    } else if (line.startsWith('* ')) {
      flushPara(key);
      if (!currentList) currentList = [];
      currentList.push(line.slice(2));
    } else {
      flushList(key);
      currentPara.push(line);
    }
  });
  flushPara('end-p');
  flushList('end-l');
  return <div>{blocks}</div>;
}
